import { useEffect, useMemo, useState } from 'react';
import CpmCard from '../../components/design-system/Card';
import CpmChip from '../../components/design-system/Chip';
import CpmPageShell from '../../components/design-system/PageShell';
import { listInternalPeople, updateListOnNewBrands } from '../../services/internalPeopleService';
import type { InternalPerson } from '../../types/accessManagement.types';
import InternalPeopleTable from './components/InternalPeopleTable';
import { INTERNAL_PEOPLE_COPY } from './internalPeopleCopy';

const SEARCH_DEBOUNCE_MS = 300;

type PeopleSortKey = 'name' | 'phone' | 'on';

function phoneDigits(value: string): string {
    return String(value || '').replace(/\D/g, '');
}

function comparePeople(key: PeopleSortKey, a: InternalPerson, b: InternalPerson): number {
    if (key === 'name') {
        return a.name.localeCompare(b.name);
    }
    if (key === 'phone') {
        return phoneDigits(a.phone).localeCompare(phoneDigits(b.phone));
    }
    if (a.listOnNewBrands === b.listOnNewBrands) {
        return a.name.localeCompare(b.name);
    }
    return a.listOnNewBrands ? -1 : 1;
}

interface CardCounts {
    total: number;
    onCount: number;
    offCount: number;
}

interface RowError {
    message: string;
    next: boolean;
}

function digitsOnlyQuery(value: string): string {
    const trimmed = value.trim();
    if (trimmed && /^[\d\s]+$/.test(trimmed)) {
        return trimmed.replace(/\s/g, '');
    }
    return trimmed;
}

function FunnelIcon({ active }: { active: boolean }) {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={active ? 'color-mix(in oklab, #6E64F0 13%, white)' : 'none'}
            stroke={active ? '#6E64F0' : 'var(--cpm-text-secondary)'}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}

export default function InternalPeople() {
    const [searchInput, setSearchInput] = useState('');
    const [searchText, setSearchText] = useState('');
    const [people, setPeople] = useState<InternalPerson[]>([]);
    const [counts, setCounts] = useState<CardCounts | null>(null);
    const [listState, setListState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [filter, setFilter] = useState<'on' | 'off' | null>(null);
    const [sortKey, setSortKey] = useState<PeopleSortKey>('name');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [pending, setPending] = useState<Record<string, boolean>>({});
    const [rowErrors, setRowErrors] = useState<Record<string, RowError>>({});
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearchText(digitsOnlyQuery(searchInput));
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setListState((current) => (current === 'ready' ? current : 'loading'));
            try {
                const data = await listInternalPeople(searchText || undefined);
                if (cancelled) return;
                const rows = Array.isArray(data?.adminUsers) ? data.adminUsers : [];
                setPeople(
                    rows.map((person) => ({
                        _id: person._id,
                        name: person.name || '',
                        phone: person.phone || '',
                        listOnNewBrands: person.listOnNewBrands === true,
                        brandCount: person.brandCount,
                        status: person.status,
                    }))
                );
                setCounts({
                    total: typeof data?.total === 'number' ? data.total : rows.length,
                    onCount: typeof data?.onCount === 'number' ? data.onCount : 0,
                    offCount: typeof data?.offCount === 'number' ? data.offCount : 0,
                });
                setListState('ready');
            } catch {
                if (cancelled) return;
                setListState('error');
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [searchText, reloadKey]);

    const rows = useMemo(() => {
        const filtered = people.filter((person) => {
            if (filter === 'on') return person.listOnNewBrands === true;
            if (filter === 'off') return person.listOnNewBrands !== true;
            return true;
        });
        const sign = sortDir === 'asc' ? 1 : -1;
        return filtered
            .slice()
            .sort((left, right) => sign * comparePeople(sortKey, left, right));
    }, [people, filter, sortKey, sortDir]);

    const sortBy = (key: PeopleSortKey) => {
        if (sortKey === key) {
            setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir('asc');
    };

    const saveSwitch = async (person: InternalPerson, next: boolean) => {
        if (pending[person._id]) return;
        setPending((current) => ({ ...current, [person._id]: true }));
        setRowErrors((current) => {
            const nextErrors = { ...current };
            delete nextErrors[person._id];
            return nextErrors;
        });
        try {
            const saved = await updateListOnNewBrands({
                adminUserID: person._id,
                listOnNewBrands: next,
            });
            const nowOn = saved.listOnNewBrands === true;
            const wasOn = person.listOnNewBrands === true;
            setPeople((current) =>
                current.map((row) =>
                    row._id === person._id ? { ...row, listOnNewBrands: nowOn } : row
                )
            );
            if (wasOn !== nowOn) {
                setCounts((current) => {
                    if (!current) return current;
                    return {
                        ...current,
                        onCount: current.onCount + (nowOn ? 1 : -1),
                        offCount: current.offCount + (nowOn ? -1 : 1),
                    };
                });
            }
        } catch {
            setRowErrors((current) => ({
                ...current,
                [person._id]: { message: INTERNAL_PEOPLE_COPY.ROW_ERROR, next },
            }));
        } finally {
            setPending((current) => {
                const nextPending = { ...current };
                delete nextPending[person._id];
                return nextPending;
            });
        }
    };

    const retryRow = (person: InternalPerson) => {
        const error = rowErrors[person._id];
        if (!error) return;
        void saveSwitch(person, error.next);
    };

    const searching = searchText.length > 0;
    const cardsReady = counts !== null;
    const rowNoun = rows.length === 1 ? INTERNAL_PEOPLE_COPY.PERSON : INTERNAL_PEOPLE_COPY.PEOPLE;
    const countKnown = listState === 'ready';

    const setCardFilter = (next: 'on' | 'off') => {
        setFilter((current) => (current === next ? null : next));
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchText('');
    };

    return (
        <CpmPageShell maxWidth={1440} gap={14} padding="22px 34px 40px">
            <h1
                style={{
                    margin: 0,
                    fontSize: 25,
                    fontWeight: 800,
                    letterSpacing: '-0.022em',
                    lineHeight: 1.1,
                    color: 'var(--cpm-text-primary)',
                }}
            >
                {INTERNAL_PEOPLE_COPY.TITLE}
            </h1>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1fr)',
                    gridTemplateRows: 'auto auto auto',
                    columnGap: 14,
                }}
            >
                <div
                    style={{
                        gridRow: 'span 3',
                        display: 'grid',
                        gridTemplateRows: 'subgrid',
                        padding: '13px 18px 11px',
                        borderRadius: 16,
                        background: 'var(--cpm-gradient-hero)',
                        boxShadow: '0 1px 2px rgba(14,8,88,0.04), 0 12px 32px -8px rgba(14,8,88,0.28)',
                        color: '#fff',
                    }}
                >
                    <div
                        style={{
                            marginBottom: 6,
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.72)',
                        }}
                    >
                        {INTERNAL_PEOPLE_COPY.CARD_PEOPLE}
                    </div>
                    {cardsReady ? (
                        <div
                            style={{
                                alignSelf: 'baseline',
                                fontSize: 38,
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                lineHeight: 1,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {counts.total}
                        </div>
                    ) : (
                        <div
                            style={{
                                alignSelf: 'baseline',
                                height: 32,
                                width: 72,
                                borderRadius: 9,
                                background: 'rgba(255,255,255,0.16)',
                            }}
                        />
                    )}
                    <div style={{ marginTop: 6, minHeight: 32, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.78)' }}>
                        {searching ? INTERNAL_PEOPLE_COPY.CAPTION_ALL_SEARCH : INTERNAL_PEOPLE_COPY.CAPTION_ALL}
                    </div>
                </div>
                <FilterCard
                    label={INTERNAL_PEOPLE_COPY.CARD_ON}
                    count={counts?.onCount}
                    ready={cardsReady}
                    active={filter === 'on'}
                    caption={filter === 'on' ? INTERNAL_PEOPLE_COPY.CAPTION_FILTERING : INTERNAL_PEOPLE_COPY.CAPTION_ON}
                    onClick={() => setCardFilter('on')}
                />
                <FilterCard
                    label={INTERNAL_PEOPLE_COPY.CARD_OFF}
                    count={counts?.offCount}
                    ready={cardsReady}
                    active={filter === 'off'}
                    caption={filter === 'off' ? INTERNAL_PEOPLE_COPY.CAPTION_FILTERING : INTERNAL_PEOPLE_COPY.CAPTION_OFF}
                    onClick={() => setCardFilter('off')}
                />
            </div>

            <CpmCard style={{ overflow: 'visible' }}>
                <div
                    className="flex flex-wrap items-center"
                    style={{ gap: 10, padding: '14px 16px' }}
                >
                    <label className="relative" style={{ width: 320 }}>
                        <span
                            className="pointer-events-none absolute"
                            style={{ left: 12, top: 11, color: 'var(--cpm-text-tertiary)' }}
                        >
                            <SearchIcon />
                        </span>
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder={INTERNAL_PEOPLE_COPY.SEARCH_PLACEHOLDER}
                            aria-label={INTERNAL_PEOPLE_COPY.SEARCH_PLACEHOLDER}
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                height: 40,
                                padding: '0 12px 0 38px',
                                borderRadius: 10,
                                border: '1.5px solid var(--cpm-border-strong)',
                                background: '#fff',
                                fontSize: 13.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-primary)',
                                outline: 'none',
                            }}
                        />
                    </label>
                    {filter ? (
                        <CpmChip variant="token" onRemove={() => setFilter(null)}>
                            {filter === 'on' ? INTERNAL_PEOPLE_COPY.CARD_ON : INTERNAL_PEOPLE_COPY.CARD_OFF}
                        </CpmChip>
                    ) : null}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 5 }}>
                        {listState === 'loading' ? (
                            <span className="cpm-sk" style={{ width: 110, height: 12 }} />
                        ) : null}
                        {countKnown ? (
                            <>
                                <span
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 800,
                                        color: 'var(--cpm-text-primary)',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {rows.length}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                                    {rowNoun} {INTERNAL_PEOPLE_COPY.IN_THE_LIST}
                                </span>
                            </>
                        ) : null}
                    </div>
                </div>
                <InternalPeopleTable
                    rows={listState === 'ready' ? rows : []}
                    listState={listState}
                    hasPeople={people.length > 0}
                    searching={searching}
                    filter={filter}
                    searchLabel={searchText}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={sortBy}
                    pending={pending}
                    rowErrors={rowErrors}
                    onToggle={(person, next) => {
                        void saveSwitch(person, next);
                    }}
                    onRetryRow={retryRow}
                    onRetryList={() => {
                        setListState('loading');
                        setReloadKey((key) => key + 1);
                    }}
                    onClearSearch={clearSearch}
                    onClearFilter={() => setFilter(null)}
                />
            </CpmCard>
        </CpmPageShell>
    );
}

function FilterCard({
    label,
    count,
    ready,
    active,
    caption,
    onClick,
}: {
    label: string;
    count: number | undefined;
    ready: boolean;
    active: boolean;
    caption: string;
    onClick: () => void;
}) {
    const [hover, setHover] = useState(false);
    const borderColor = active || hover ? '#6E64F0' : 'var(--cpm-border-default)';
    return (
        <button
            type="button"
            aria-pressed={active}
            disabled={!ready}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                gridRow: 'span 3',
                display: 'grid',
                gridTemplateRows: 'subgrid',
                textAlign: 'left',
                fontFamily: 'inherit',
                padding: '13px 17px 11px',
                borderRadius: 16,
                border: `1px solid ${borderColor}`,
                background: active ? 'var(--cpm-purple-selected)' : '#fff',
                boxShadow: 'var(--cpm-shadow-card)',
                cursor: ready ? 'pointer' : 'default',
                color: 'var(--cpm-text-primary)',
                transition: 'border-color 150ms ease, background 150ms ease',
            }}
        >
            <div className="flex items-start justify-between" style={{ gap: 8, marginBottom: 6 }}>
                <span
                    style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {label}
                </span>
                <FunnelIcon active={active} />
            </div>
            {ready ? (
                <div
                    style={{
                        alignSelf: 'baseline',
                        fontSize: 30,
                        fontWeight: 800,
                        letterSpacing: '-0.025em',
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {count ?? 0}
                </div>
            ) : (
                <div
                    className="cpm-sk"
                    style={{ alignSelf: 'baseline', height: 26, width: 56, borderRadius: 7 }}
                />
            )}
            <div style={{ marginTop: 6, minHeight: 32, fontSize: 12, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                {caption}
            </div>
        </button>
    );
}
